import { create } from 'zustand';
import { createDictationSlice } from './createDictationSlice';
import { createRecordingSlice } from './createRecordingSlice';
import { createSoundsSlice } from './createSoundsSlice';
import { setDictationIdReader } from './dictationLogger';
import { type DictationStore } from './types';
import {
  selectUniversalDictationShouldBlockRefresh,
  setUniversalDictationShouldBlockRefresh,
} from './universal-dictation-refresh-guard';

export const useDictationStore = create<DictationStore>((set, get, api) => {
  // Keep a bundle-light refresh guard synchronized for safety checks outside the
  // universal-dictation feature graph. The store's initial state is inactive and
  // matches the guard's default, so the subscription alone keeps them in sync.
  api.subscribe((state) => {
    setUniversalDictationShouldBlockRefresh(
      selectUniversalDictationShouldBlockRefresh(state)
    );
  });

  // Register the store-backed reader while the store is constructed
  // so `dictationLogger` can auto-enrich every payload with the current
  // session's correlation IDs. Lives here (rather than in dictationLogger.ts)
  // to avoid the circular import that would otherwise pull useDictationStore
  // into every slice's module-init path. See [[dictationLogger.ts]] for why.
  setDictationIdReader(() => {
    const state = get();
    return {
      dictationId: state.dictationId,
      dictationRecordingId: state.dictationRecordingId,
    };
  });

  return {
    ...createDictationSlice(set, get, api),
    ...createRecordingSlice(set, get, api),
    ...createSoundsSlice(set, get, api),
  };
});

/** Gets current dictation mode from store. */
export const getDictationMode = () => useDictationStore.getState().dictationMode;

/** Gets current dictation status from store. */
export const getDictationStatus = () => useDictationStore.getState().status;
